import React, { useState } from 'react';
import { CheckCircle2, Circle, Download, FileText, AlertCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ImportTitreNotice } from '../components/ImportTitreNotice';
import { ImportTitreGuideLink } from '../components/ImportTitreGuideLink';
import { IMPORT_CONDITIONS_FILENAME, IMPORT_CONDITIONS_URL } from '../lib/importConditions';

export const ImportChecklist = () => {
  const navigate = useNavigate();
  const [checkedItems, setCheckedItems] = useState({});

  // Color themes aligned with site color scheme
  const sectionThemes = [
    { 
      gradient: 'from-teal-500 to-cyan-500',
      lightGradient: 'from-teal-500/10 to-cyan-500/10',
      iconBg: 'bg-gradient-to-br from-teal-500 to-cyan-600',
      iconColor: 'text-teal-600',
      border: 'border-teal-500/30'
    },
    { 
      gradient: 'from-blue-500 to-indigo-500',
      lightGradient: 'from-blue-500/10 to-indigo-500/10',
      iconBg: 'bg-gradient-to-br from-blue-500 to-indigo-600',
      iconColor: 'text-blue-600',
      border: 'border-blue-500/30'
    },
    { 
      gradient: 'from-cyan-500 to-blue-500',
      lightGradient: 'from-cyan-500/10 to-blue-500/10',
      iconBg: 'bg-gradient-to-br from-cyan-500 to-blue-600',
      iconColor: 'text-cyan-600',
      border: 'border-cyan-500/30'
    },
    { 
      gradient: 'from-emerald-500 to-teal-500',
      lightGradient: 'from-emerald-500/10 to-teal-500/10',
      iconBg: 'bg-gradient-to-br from-emerald-500 to-teal-600',
      iconColor: 'text-emerald-600',
      border: 'border-emerald-500/30'
    },
    { 
      gradient: 'from-sky-500 to-cyan-500',
      lightGradient: 'from-sky-500/10 to-cyan-500/10',
      iconBg: 'bg-gradient-to-br from-sky-500 to-cyan-600',
      iconColor: 'text-sky-600',
      border: 'border-sky-500/30'
    },
    { 
      gradient: 'from-indigo-500 to-purple-500',
      lightGradient: 'from-indigo-500/10 to-purple-500/10',
      iconBg: 'bg-gradient-to-br from-indigo-500 to-purple-600',
      iconColor: 'text-indigo-600',
      border: 'border-indigo-500/30'
    },
    { 
      gradient: 'from-blue-500 to-cyan-500',
      lightGradient: 'from-blue-500/10 to-cyan-500/10',
      iconBg: 'bg-gradient-to-br from-blue-500 to-cyan-600',
      iconColor: 'text-blue-600',
      border: 'border-blue-500/30'
    }
  ];

  const checklistSections = [
    {
      id: 'microchipping',
      title: 'Microchipping Requirements',
      items: [
        {
          id: 'mc1',
          text: 'My pet has been microchipped with an ISO-compliant (15 digit, numerical) microchip if implanted on or after March 1st, 2019, OR with an ISO-compatible (10+ alphanumerical characters) microchip if implanted prior to this date.'
        },
        {
          id: 'mc2',
          text: 'I have proof of the date of microchip implant (proof of payment, microchip implantation certificate, or copy of pet\'s health record).'
        },
        {
          id: 'mc3',
          text: 'The microchip can be read (verified with veterinarian).'
        }
      ]
    },
    {
      id: 'photo',
      title: 'Photograph',
      items: [
        {
          id: 'ph1',
          text: 'I have included a current, colour, full-body photo of my pet(s).'
        }
      ]
    },
    {
      id: 'rabies-vaccination',
      title: 'Rabies Vaccination',
      items: [
        {
          id: 'rv1',
          text: 'My pet was vaccinated in accordance with the approved guidelines (see official documentation).'
        },
        {
          id: 'rv2',
          text: 'The rabies vaccination certificate includes make, serial number, date of expiration, and period of validity.'
        },
        {
          id: 'rv3',
          text: 'My pet was vaccinated against rabies AFTER the microchip was implanted.',
          note: 'Note: The microchip and rabies vaccination can be done at the same appointment.'
        },
        {
          id: 'rv4',
          text: 'The microchip number has been entered correctly on the rabies vaccination certificate.'
        }
      ]
    },
    {
      id: 'rabies-titre',
      title: 'Rabies Titre Test (FAVN Test)',
      items: [
        {
          id: 'rt1',
          text: 'The blood sample was taken at least 30 days after the rabies vaccination.'
        },
        {
          id: 'rt2',
          text: 'My pet has had a satisfactory blood test at an approved laboratory performed within the last 12 months (serum antibody level ≥ 0.5 IU/ml).'
        },
        {
          id: 'rt3',
          text: 'I have a copy of the blood test result showing the correct microchip number and the date the blood was drawn.'
        }
      ]
    },
    {
      id: 'health-certificate',
      title: 'Veterinary Health Certificate',
      items: [
        {
          id: 'hc1',
          text: 'A licensed/accredited veterinarian has correctly completed the veterinary health certificate.'
        },
        {
          id: 'hc2',
          text: 'The certificate has been endorsed by an official government veterinarian (if legally required by the exporting country).'
        },
        {
          id: 'hc3',
          text: 'I have received and will travel with the ORIGINAL health certificate.'
        },
        {
          id: 'hc4',
          text: 'When travelling, I will have my pet\'s vaccination record and, if required, blood test results, showing the microchip number.'
        }
      ]
    },
    {
      id: 'treatments',
      title: 'Tick and Tapeworm Treatment',
      items: [
        {
          id: 'tr1',
          text: 'My pet was treated on the day of veterinary examination with approved treatments within 14 days prior to travel.'
        },
        {
          id: 'tr2',
          text: 'The products used to treat for ticks and tapeworms have been recorded on the veterinary health certificate.'
        }
      ]
    },
    {
      id: 'travel',
      title: 'Travel Requirements',
      items: [
        {
          id: 'tv1',
          text: 'If travelling through a prohibited country, my pet\'s crate/container has been officially sealed.'
        },
        {
          id: 'tv2',
          text: 'My pet will travel in an IATA-approved container that conforms to IATA rules and regulations.'
        }
      ]
    }
  ];

  const handleCheckItem = (itemId) => {
    setCheckedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const getTotalItems = () => {
    return checklistSections.reduce((total, section) => total + section.items.length, 0);
  };

  const getCheckedCount = () => {
    return Object.values(checkedItems).filter(Boolean).length;
  };

  const getProgress = () => {
    const total = getTotalItems();
    const checked = getCheckedCount();
    return total > 0 ? Math.round((checked / total) * 100) : 0;
  };

  return (
    <div data-testid="import-checklist-page" className="min-h-screen bg-gradient-to-b from-muted/30 to-background py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              data-testid="import-checklist-back-home"
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back to Home</span>
            </button>

            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-8 shadow-xl border-2 border-emerald-400">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 px-4 py-2 rounded-full mb-4">
                    <FileText className="w-4 h-4 text-white" />
                    <span className="text-xs font-bold text-white uppercase tracking-wide">Import Requirements</span>
                  </div>
                  <h1 data-testid="import-checklist-title" className="text-3xl sm:text-4xl font-bold text-white mb-3">
                    Pet Import Checklist for Cayman Islands
                  </h1>
                  <p className="text-sm sm:text-base text-emerald-50">
                    Use this comprehensive checklist to ensure you meet all requirements for importing your dog or cat into the Cayman Islands
                  </p>
                </div>
                <a
                  href={IMPORT_CONDITIONS_URL}
                  download={IMPORT_CONDITIONS_FILENAME}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="import-conditions-header-download"
                  aria-label="Download updated official importation conditions PDF"
                  className="self-start flex-shrink-0 bg-white hover:bg-emerald-50 text-emerald-700 px-4 py-2 rounded-lg font-semibold transition-colors inline-flex items-center gap-2 text-sm shadow-lg"
                >
                  <Download className="w-4 h-4" />
                  <span>Official PDF</span>
                </a>
              </div>

              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-emerald-50">Progress</span>
                  <span data-testid="import-checklist-progress-count" className="font-bold text-white">{getCheckedCount()} of {getTotalItems()} completed</span>
                </div>
                <div data-testid="import-checklist-progress-bar" role="progressbar" aria-label="Checklist completion" aria-valuemin={0} aria-valuemax={100} aria-valuenow={getProgress()} className="w-full bg-emerald-800/40 rounded-full h-3 overflow-hidden border border-emerald-400/30">
                  <div
                    className="h-full bg-gradient-to-r from-white to-emerald-100 transition-all duration-500 rounded-full shadow-inner"
                    style={{ width: `${getProgress()}%` }}
                  />
                </div>
                <p data-testid="import-checklist-progress-percentage" className="text-xs text-emerald-100 text-right">{getProgress()}% Complete</p>
              </div>
            </div>
          </div>

          {/* Important Notice */}
          <div data-testid="import-checklist-important-information" className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-amber-900 mb-2">Important Information</h3>
                <ul className="text-sm text-amber-800 space-y-1 leading-relaxed">
                  <li>• Applications should be submitted up to 6 months in advance of travel</li>
                  <li>• The Health Certificate is valid for only 14 days from examination date</li>
                  <li>• All procedures must be done AFTER microchip implantation</li>
                  <li>• Contact <span className="font-semibold">Pawsome Ventures</span> (<a data-testid="import-checklist-pawsome-email" href="mailto:pawsome.ky@gmail.com" className="underline hover:text-amber-900">pawsome.ky@gmail.com</a>) or the Cayman Islands Department of Agriculture (<a data-testid="import-checklist-government-email" href="mailto:ci.agriculturevs@gov.ky" className="underline hover:text-amber-900">ci.agriculturevs@gov.ky</a>) for clarification</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Checklist Sections */}
          <div className="space-y-6">
            {checklistSections.map((section, sectionIndex) => {
              const theme = sectionThemes[sectionIndex % sectionThemes.length];
              
              return (
                <div key={section.id} id={section.id} data-testid={`import-checklist-section-${section.id}`} className={`bg-white rounded-2xl shadow-xl border-2 ${theme.border} overflow-hidden hover:shadow-2xl transition-all duration-300`}>
                  <div className={`bg-gradient-to-r ${theme.lightGradient} px-6 py-5 border-b-2 ${theme.border}`}>
                    <h2 data-testid={`import-checklist-heading-${section.id}`} className="text-xl font-bold text-foreground flex items-center gap-3">
                      <span className={`flex flex-shrink-0 items-center justify-center w-10 h-10 rounded-xl ${theme.iconBg} text-white text-lg font-bold shadow-lg`}>
                        {sectionIndex + 1}
                      </span>
                      {section.title}
                    </h2>
                  </div>
                  <div className="p-6">
                    {section.id === 'rabies-titre' && (
                      <>
                        <ImportTitreNotice />
                        <ImportTitreGuideLink />
                        <p data-testid="import-favn-applicability-note" className="mb-4 text-sm font-medium text-foreground/70">
                          Complete the items below only if titre testing applies to your pet.
                        </p>
                      </>
                    )}
                    <div className="space-y-4">
                      {section.items.map((item) => (
                        <div key={item.id}>
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked={Boolean(checkedItems[item.id])}
                            aria-label={item.text}
                            data-testid={`import-checklist-item-${item.id}`}
                            onClick={() => handleCheckItem(item.id)}
                            className={`w-full text-left flex items-start gap-3 p-4 rounded-xl hover:bg-gradient-to-r hover:${theme.lightGradient} cursor-pointer transition-all duration-200 border-2 ${
                              checkedItems[item.id] ? `${theme.border} bg-gradient-to-r ${theme.lightGradient}` : 'border-transparent hover:border-border'
                            }`}
                          >
                            <div className="flex-shrink-0 mt-0.5">
                              {checkedItems[item.id] ? (
                                <div className={`w-7 h-7 rounded-lg ${theme.iconBg} flex items-center justify-center shadow-md`}>
                                  <CheckCircle2 className="w-5 h-5 text-white" />
                                </div>
                              ) : (
                                <Circle className={`w-7 h-7 ${theme.iconColor} transition-colors`} />
                              )}
                            </div>
                            <div className="flex-1">
                              <p data-testid={`import-checklist-text-${item.id}`} className={`text-sm sm:text-base leading-relaxed ${
                                checkedItems[item.id] ? 'text-foreground/60 line-through' : 'text-foreground/80'
                              }`}>
                                {item.text}
                              </p>
                              {item.note && (
                                <p data-testid={`import-checklist-note-${item.id}`} className="text-xs sm:text-sm text-primary/80 mt-2 italic font-medium">
                                  💡 {item.note}
                                </p>
                              )}
                            </div>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Completion Message */}
          {getProgress() === 100 && (
            <div data-testid="import-checklist-completion-message" className="mt-8 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full mb-4">
                  <CheckCircle2 className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-green-900 mb-2">Checklist Complete! 🎉</h3>
                <p className="text-green-800 mb-6">
                  You've checked all items. Make sure to have all documentation ready before travel.
                </p>
                <a
                  data-testid="import-checklist-completion-contact"
                  href="/#contact"
                  className="inline-block bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Contact Us for Assistance
                </a>
              </div>
            </div>
          )}

          {/* Footer CTA */}
          <div className="mt-12 bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 text-center border-2 border-primary/20">
            <h3 className="text-2xl font-bold text-foreground mb-3">
              Need Professional Assistance?
            </h3>
            <p className="text-foreground/70 mb-6 max-w-2xl mx-auto">
              Let Pawsome Ventures handle the complex import process for you. We ensure all requirements are met correctly and on time.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                data-testid="import-checklist-expert-help"
                href="/#contact"
                className="bg-primary hover:bg-primary/90 text-white px-8 py-3 rounded-xl font-bold transition-all cursor-pointer text-center inline-block"
              >
                Get Expert Help
              </a>
              <a
                href={IMPORT_CONDITIONS_URL}
                download={IMPORT_CONDITIONS_FILENAME}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="import-conditions-footer-download"
                className="bg-white hover:bg-muted text-primary border-2 border-primary px-8 py-3 rounded-xl font-bold transition-colors inline-flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" />
                Download Official Government PDF
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
